import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL_BASE } from './config';
import ConfirmDialog from './components/ConfirmDialog';
import './css/dashboard_style.css';

function AdminHome() {
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessageState] = useState('');

  const setMessage = (msg) => {
    setMessageState(msg);
    setTimeout(() => setMessageState(''), 3000);
  };
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  useEffect(() => {
    if (role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchAllEvents();
  }, []);

  const fetchUsers = () => {
    fetch(API_URL_BASE + '/ticket.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fetchallusers: { token } }),
    })
      .then(res => res.json())
      .then(data => setUsers(data.users?.users || []))
      .catch(err => console.error('Error fetching users:', err))
      .finally(() => setLoading(false));
  };

  const fetchAllEvents = () => {
    fetch(API_URL_BASE + '/ticket.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fetchallevents: { token: 'ALL' } }),
    })
      .then(res => res.json())
      .then(data => setEvents(data.events?.events || []))
      .catch(err => console.error('Error fetching events:', err));
  };

  const handleActivate = async (userid) => {
    try {
      const res = await fetch(API_URL_BASE + '/ticket.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activateuser: { token, id: userid } }),
      });
      const data = await res.json();
      if (data.message === 'ACTIVATED') {
        setMessage('User activated successfully.');
        fetchUsers();
      }
    } catch (err) {
      setMessage('Failed to activate user.');
    }
  };

  const handleDeactivate = async (userid) => {
    try {
      const res = await fetch(API_URL_BASE + '/ticket.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deactivateuser: { token, id: userid } }),
      });
      const data = await res.json();
      if (data.message === 'DEACTIVATED') {
        setMessage('User deactivated successfully.');
        fetchUsers();
      }
    } catch (err) {
      setMessage('Failed to deactivate user.');
    }
  };

  const confirmDeactivate = (userid) => {
    setPendingDeactivate(userid);
    setShowConfirm(true);
  };

  const handleConfirmDeactivate = async (e) => {
    e.preventDefault();
    setShowConfirm(false);
    await handleDeactivate(pendingDeactivate);
    setPendingDeactivate(null);
  };

  return (
    <div className="main-content">
      <h2>Admin Panel</h2>
      {message && <p className="flash-message">{message}</p>}

      {showConfirm && (
        <ConfirmDialog
          title="Deactivate User"
          message="Are you sure you want to deactivate this user? They will no longer be able to log in."
          onConfirm={handleConfirmDeactivate}
          onCancel={() => { setShowConfirm(false); setPendingDeactivate(null); }}
        />
      )}

      <section>
        <h3>Organisers</h3>
        {loading ? <p>Loading...</p> : (
          <div className="table-responsive">
            <table className="events-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.filter(u => u.role !== 'ADMIN').map(user => {
                  const isSelected = expandedUserId === user.userid;
                  return (
                    <tr
                      key={user.userid}
                      className={`admin-organiser-row${isSelected ? ' admin-organiser-row--selected' : ''}`}
                      onClick={() => setExpandedUserId(isSelected ? null : user.userid)}
                      title="Click to filter events"
                    >
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.role || 'ORGANISER'}</td>
                      <td>{user.active === 'Y' ? '✅ Active' : '🔴 Inactive'}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {user.active !== 'Y' ? (
                          <button className="admin-action-btn admin-btn-activate" onClick={() => handleActivate(user.userid)}>
                            Activate
                          </button>
                        ) : (
                          <button className="admin-action-btn admin-btn-deactivate" onClick={() => confirmDeactivate(user.userid)}>
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ marginTop: '2rem' }}>
        <h3>
          {expandedUserId
            ? `Events — ${users.find(u => u.userid === expandedUserId)?.name || expandedUserId}`
            : 'All Events'}
          {expandedUserId && (
            <span
              className="admin-clear-filter"
              onClick={() => setExpandedUserId(null)}
              title="Show all events"
            > ✕ clear</span>
          )}
        </h3>
        <div className="table-responsive">
          <table className="events-table">
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Start</th>
				<th>Event ID</th>
              </tr>
            </thead>
            <tbody>
              {(expandedUserId
                ? events.filter(e => e.userid === expandedUserId)
                : events
              ).map(event => (
                <tr key={event.eventid}>
                  <td>{event.name}</td>
                  <td>{event.type}</td>
                  <td>{event.status}</td>
                  <td>{new Date(event.startdatetime).toLocaleDateString()}</td>
				  <td>{event.eventid}</td>
                </tr>
              ))}
              {expandedUserId && events.filter(e => e.userid === expandedUserId).length === 0 && (
                <tr><td colSpan={4} style={{ color: '#6b7280', textAlign: 'center' }}>No events for this organiser.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminHome;
